import {
  OnInit,
  Component,
  Input
} from '@angular/core';
import { LMFormatUtilService } from '@services/utils';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';

@Component({
  selector:    'lm-list-chart-selectors',
  templateUrl: 'lm-list-chart-selectors.component.html'
})
export class LMListChartSelectorsComponent implements OnInit {

  @Input() set listSpec(listSpec) {
    this._listSpec = listSpec;
  };

  get listSpec(): LMListSpec {
    return this._listSpec;
  };

  @Input() set dataViewSet(dataViewSet) {
    this._dataViewSet = dataViewSet;
  };

  get dataViewSet(): LMDataViewSet {
    return this._dataViewSet;
  };

  @Input() set selectedDimension(selectedDimension) {
    this._selectedDimension = selectedDimension;
  };

  get selectedDimension(): string {
    return this._selectedDimension;
  };

  @Input() set selectedTotal(selectedTotal) {
    this._selectedTotal = selectedTotal;
  };

  get selectedTotal(): string {
    return this._selectedTotal;
  };

  @Input() set selectedOrderBy(selectedOrderBy) {
    this._selectedOrderBy = selectedOrderBy;
  };

  get selectedOrderBy(): string {
    if (this._selectedOrderBy && this.totals.findIndex(x => x.value === this._selectedOrderBy) === -1) {
      return this.selectedDimension;
    }

    return this._selectedOrderBy;
  };

  @Input() set isAscending(isAscending) {
    this._isAscending = isAscending;
  };

  get isAscending(): boolean {
    return this._isAscending;
  };

  @Input() set listState(listState) {
    this._listState = listState;
  };

  get listState(): LMListState {
    return this._listState;
  };

  @Input() set chartName(chartName) {
    this._chartName = chartName;
  };

  get chartName(): string {
    return this._chartName;
  };

  constructor(
    private lmFormatUtilService: LMFormatUtilService,
    private lmUniqueIdService: LMUniqueIdService
  ) {}

  _listSpec: LMListSpec;
  _dataViewSet: LMDataViewSet;
  _selectedDimension: string;
  _dimensionId: string;
  _selectedTotal: string;
  _totalId: string;
  _selectedOrderBy: string;
  _orderById: string;
  _isAscending: boolean;
  _isAscendingId: string;
  _listState: LMListState;
  _chartName: string;
  _dimensions: LMStateValue[] = [];
  _totals: LMStateValue[] = [];
  _orderBy: LMStateValue[] = [];
  _dimensionChanged = false;
  _totalChanged = false;
  _orderByChanged = false;
  _isAscendingChanged = false;

  set dimensions(dimensions) {
    this._dimensions = dimensions;
  };

  get dimensions(): LMStateValue[] {
    return this._dimensions;
  };

  get dimensionId(): string {
    return this._dimensionId;
  }

  set totals(totals) {
    this._totals = totals;
  };

  get totals(): LMStateValue[] {
    return this._totals;
  }

  get totalId(): string {
    return this._totalId;
  }

  set orderBy(orderBy) {
    this._orderBy = orderBy;
  };

  get orderBy(): LMStateValue[] {
    return this._orderBy;
  };

  get orderById(): string {
    return this._orderById;
  }

  get isAscendingId(): string {
    return this._isAscendingId;
  }

  set dimensionChanged(dimensionChanged) {
    this._dimensionChanged = dimensionChanged;
  };

  get dimensionChanged(): boolean {
    return this._dimensionChanged;
  };

  set totalChanged(totalChanged) {
    this._totalChanged = totalChanged;
  };

  get totalChanged(): boolean {
    return this._totalChanged;
  };

  set orderByChanged(orderByChanged) {
    this._orderByChanged = orderByChanged;
  };

  get orderByChanged(): boolean {
    return this._orderByChanged;
  };

  set isAscendingChanged(isAscendingChanged) {
    this._isAscendingChanged = isAscendingChanged;
  };

  get isAscendingChanged(): boolean {
    return this._isAscendingChanged;
  };

  ngOnInit() {
    const listId = this.listSpec.busListSpec.id;
    this._dimensionId = this.lmUniqueIdService.uniqueId([listId, 'dimension']);
    this._totalId = this.lmUniqueIdService.uniqueId([listId, 'total']);
    this._orderById = this.lmUniqueIdService.uniqueId([listId, 'orderBy']);
    this._isAscendingId = this.lmUniqueIdService.uniqueId([listId, 'isAscending']);

    for (let i = 0; i < this.listSpec.busListSpec.displayFields.length; i++) {
      const field: LMListDisplayField = this.listSpec.busListSpec.displayFields[ i ];
      const value: string = this.lmFormatUtilService.formatMessageRule(field.label, this.dataViewSet.header);
      if (field.isTotal) {
        this.totals.push({ value: field.fieldId, label: value });
        this.orderBy.push({ value: field.fieldId, label: value });
      } else {
        let cantJoin: Boolean = false;

        if (this.dataViewSet.columnarInfo.cantJoinList) {
          for (const rfsId of this.dataViewSet.columnarInfo.cantJoinList) {
            if (field.fieldId === rfsId) {
              cantJoin = true;
            }
          }
        }

        if (!cantJoin) {
          this.dimensions.push({ value: field.fieldId, label: value });
        }
      }

      if (field.fieldId === this.dataViewSet.columnarInfo.defaultDimension) {
        this.orderBy.unshift({ value: field.fieldId, label: value });
      }
    }

    if (this.totals.length > 1
    &&  this.listSpec.busListSpec.charts[ this._chartName ].chartType !== 'pie') {
      this.totals.unshift({value: 'COLUMNAR_All', label: 'All'});
      this.selectedTotal = 'COLUMNAR_All';
    }

    if (this.listState.columnarOptions) {
      if (this.listState.columnarOptions.selectedDimension) {
        const selDim: LMStateValue = this.dimensions.find(x => x.value === this.listState.columnarOptions.selectedDimension);
        if (selDim) {
          this.selectedDimension = selDim.value;

          if (this.orderBy[ 0 ] !== selDim) {
            this.orderBy.shift();
            this.orderBy.unshift(selDim);
          }

          if (this.totals.findIndex(x => x.value === this.listState.columnarOptions.selectedOrderBy) === -1) {
            this.listState.columnarOptions.selectedOrderBy = selDim.value;
          }
        } else {
          this.listState.columnarOptions.selectedDimension = this.dataViewSet.columnarInfo.defaultDimension;
          this.listState.columnarOptions.selectedOrderBy = this.dataViewSet.columnarInfo.defaultDimension;
          this.selectedDimension = this.dataViewSet.columnarInfo.defaultDimension;
        }
      } else {
        this.selectedDimension = this.dataViewSet.columnarInfo.defaultDimension;
      }

      if (this.listState.columnarOptions.selectedTotal) {
        if (this.listState.columnarOptions.selectedTotal === 'COLUMNAR_All'
        &&  this.listSpec.busListSpec.charts[ this._chartName ].chartType !== 'pie') {
          this.selectedTotal = 'COLUMNAR_All'
        } else {
          this.selectedTotal = this.totals.find(x => x.value === this.listState.columnarOptions.selectedTotal).value;
        }
      } else {
        if (this.totals.length > 1
        &&  this.listSpec.busListSpec.charts[ this._chartName ].chartType !== 'pie') {
          this.selectedTotal = 'COLUMNAR_All';
        } else {
          this.selectedTotal = this.listSpec.busListSpec.charts[ this.chartName ].fields[ 0 ].fieldId;
        }
      }

      if (this.listState.columnarOptions.selectedOrderBy) {
        this.selectedOrderBy = this.orderBy.find(x => x.value === this.listState.columnarOptions.selectedOrderBy).value;
      } else {
        this.selectedOrderBy = this.dataViewSet.columnarInfo.defaultDimension;
      }

      if (this.listState.columnarOptions.isAscending !== undefined) {
        this.isAscending = this.listState.columnarOptions.isAscending;
      } else {
        this.isAscending = !this.dataViewSet.columnarInfo.isDefaultDescending;
      }

      this.listSpec = undefined;
    }
  }

  trackByValueDimension(index: number, state: LMStateValue) {
    return state.value;
  }

  trackByValueTotal(index: number, state: LMStateValue) {
    return state.value;
  }

  trackByValueOrderBy(index: number, state: LMStateValue) {
    return state.value;
  }
}
